import React, { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Loader2, Send, CheckCircle2, MessageCircle, MapPin, Clock, Wrench, AlertTriangle } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const URGENCIAS = [
  { value: "baixa", label: "Baixa - posso aguardar" },
  { value: "normal", label: "Normal - próximos dias" },
  { value: "alta", label: "Alta - amanhã" },
  { value: "critica", label: "Crítica - hoje" },
];
const HORARIOS = ["Manhã (08-12)", "Tarde (12-18)", "Comercial", "Após 18h"];

const STEPS = [
  { icon: AlertTriangle, title: "1. Solicitação", desc: "Você preenche o formulário com o problema e dados de contato." },
  { icon: MessageCircle, title: "2. Confirmação", desc: "Nossa equipe entra em contato para confirmar data e detalhes." },
  { icon: Wrench, title: "3. Diagnóstico", desc: "Técnico vai ao local com equipamentos completos para análise." },
  { icon: CheckCircle2, title: "4. Solução", desc: "Orçamento aprovado, executamos a correção e entregamos resolvido." },
];

export default function VisitaTecnica() {
  const { config } = useSite();
  const [form, setForm] = useState({ name: "", company: "", address: "", city: "", phone: "", email: "", problem_type: "", equipment: "", urgency: "normal", preferred_date: "", preferred_time: "", notes: "" });
  const [date, setDate] = useState();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !form.city || !form.problem_type) {
      toast.error("Preencha os campos obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, preferred_date: date ? format(date, "yyyy-MM-dd") : "" };
      await api.post("/visits", payload);
      setSuccess(true);
      toast.success("Visita técnica solicitada!");
    } catch (err) {
      toast.error("Erro ao enviar.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="page-visita" className="pt-32 md:pt-40 pb-20 min-h-screen bg-[#f5f7fa]">
        <div className="dc-container max-w-2xl text-center bg-white rounded-3xl p-12 shadow-lg border border-slate-100">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-6"><CheckCircle2 className="w-10 h-10 text-emerald-500" /></div>
          <h1 data-testid="visita-success" className="font-heading text-3xl md:text-4xl font-bold text-dc-text mb-3">Visita técnica agendada!</h1>
          <p className="text-slate-600 leading-relaxed mb-8">Recebemos sua solicitação. Em breve confirmamos o horário pelo telefone informado.</p>
          <a href={`https://wa.me/${config?.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-3 rounded-full font-semibold"><MessageCircle className="w-4 h-4" /> Acelerar pelo WhatsApp</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-visita" className="pt-32 md:pt-40 pb-20 bg-[#f5f7fa] min-h-screen">
      <div className="dc-container">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-dc-primary">Visita técnica</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-dc-text mt-3 mb-4 leading-tight">Diagnóstico presencial onde sua empresa precisar</h1>
          <p className="text-base text-slate-600">Agende uma visita e nossa equipe técnica vai até você com tudo que é necessário.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-dc-primary/10 text-dc-primary flex items-center justify-center mb-4"><s.icon className="w-5 h-5" strokeWidth={1.6} /></div>
              <h4 className="font-heading font-semibold text-dc-text mb-1.5">{s.title}</h4>
              <p className="text-sm text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100 space-y-5 max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nome *"><Input data-testid="visita-name" value={form.name} onChange={update("name")} placeholder="Seu nome" /></Field>
            <Field label="Empresa"><Input data-testid="visita-company" value={form.company} onChange={update("company")} placeholder="Nome da empresa" /></Field>
          </div>
          <Field label="Endereço *"><Input data-testid="visita-address" value={form.address} onChange={update("address")} placeholder="Rua, número, bairro, complemento" /></Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Cidade *"><Input data-testid="visita-city" value={form.city} onChange={update("city")} placeholder="Sua cidade/UF" /></Field>
            <Field label="Telefone *"><Input data-testid="visita-phone" value={form.phone} onChange={update("phone")} placeholder="(43) 99999-9999" /></Field>
          </div>
          <Field label="E-mail *"><Input data-testid="visita-email" type="email" value={form.email} onChange={update("email")} placeholder="seu@email.com" /></Field>
          <Field label="Tipo do problema *"><Input data-testid="visita-problem" value={form.problem_type} onChange={update("problem_type")} placeholder="Ex: rede lenta, servidor parado, computador não liga..." /></Field>
          <Field label="Equipamentos envolvidos"><Input data-testid="visita-equipment" value={form.equipment} onChange={update("equipment")} placeholder="Ex: 5 desktops, 2 servidores, switches, impressoras" /></Field>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Urgência">
              <Select value={form.urgency} onValueChange={update("urgency")}>
                <SelectTrigger data-testid="visita-urgency"><SelectValue /></SelectTrigger>
                <SelectContent>{URGENCIAS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Data preferida">
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="visita-date" type="button" variant="outline" className="w-full justify-start text-left font-normal h-10">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {date ? format(date, "PPP", { locale: ptBR }) : "Selecione uma data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} locale={ptBR} initialFocus />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Melhor horário">
              <Select value={form.preferred_time} onValueChange={update("preferred_time")}>
                <SelectTrigger data-testid="visita-time"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{HORARIOS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Observações"><Textarea data-testid="visita-notes" value={form.notes} onChange={update("notes")} rows={4} placeholder="Detalhes que possam ajudar nosso técnico..." /></Field>

          <Button data-testid="visita-submit" type="submit" disabled={loading} className="w-full bg-dc-primary hover:bg-dc-primary-hover text-white py-6 rounded-full font-semibold text-base">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4 mr-2" /> Solicitar visita técnica</>}
          </Button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-sm font-medium text-dc-text mb-2 block">{label}</Label>{children}</div>);
